import React, { useState, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import imageCompression from 'browser-image-compression';
import secureApi from '../../../utils/secureApi';
import { getImageUrl } from '../../../utils/imageUrl';

interface EditCategoryModalProps {
  show: boolean;
  handleClose: () => void;
  onSave: (data: { name: string; icon: string | null; description: string; _id?: string }) => void;
  initialData?: { name: string; icon: string | null; description: string; _id?: string };
}

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({ show, handleClose, onSave, initialData }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [icon, setIcon] = useState<string | null>(initialData?.icon || null);
  const [description, setDescription] = useState(initialData?.description || '');
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper to get dd-mm-yy
  const getDatePath = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yy = String(now.getFullYear()).slice(-2);
    return `${dd}-${mm}-${yy}`;
  };

  // Helper to sanitize filename
  const sanitizeFilename = (name: string) => {
    return (name || 'icon').replace(/[^a-zA-Z0-9-_]/g, '_');
  };

  const handleIconChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIconFile(file);
    setIsUploading(true);
    setUploadError(null);
    try {
      // Compress the image
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 0.15,
        maxWidthOrHeight: 1600,
        initialQuality: 0.92,
        useWebWorker: true
      });
      // Prepare S3 path
      const ext = compressedFile.type.split('/')[1] || 'jpg';
      const fileName = `${sanitizeFilename(name)}_${Date.now()}.${ext}`;
      const context = `blog-category/${getDatePath()}`;
      // Get presigned URL
      const response = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: compressedFile.type,
          context
        }
      );
      const presignedUrl = response.data?.presignedUrl;
      const s3Key = response.data?.key;
      if (!presignedUrl) throw new Error('Failed to get presigned URL');
      // Upload to S3
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressedFile.type },
        body: compressedFile
      });
      if (uploadResponse.ok) {
        // Store only the S3 key, not the full URL
        setIcon(s3Key);
        setUploadError(null);
      } else {
        throw new Error('Failed to upload image to S3');
      }
    } catch (err: any) {
      setUploadError('Image upload failed. Please try again.');
      setIcon(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Category name is required');
      return;
    }
    setError('');
    onSave({ name: name.trim(), icon, description: description.trim(), _id: initialData?._id });
  };

  // Helper function to check if a field has changed
  const hasFieldChanged = (currentValue: string, originalValue: string | null) => {
    const current = currentValue || '';
    const original = originalValue || '';
    return current !== original;
  };

  const handleShow = () => {
    setName(initialData?.name || '');
    setIcon(initialData?.icon || null);
    setDescription(initialData?.description || '');
    setIconFile(null);
    setError('');
    setUploadError(null);
    setIsUploading(false);
  };

  return (
    <Modal show={show} onHide={handleClose} onShow={handleShow} centered>
      <Modal.Header closeButton>
        <Modal.Title>{initialData ? 'Edit Category' : 'Add Category'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Category Name<span style={{ color: 'red' }}>*</span>
              {initialData && hasFieldChanged(name, initialData.name) && (
                <span style={{ color: 'orange', fontSize: '12px', marginLeft: '8px' }}>• Modified</span>
              )}
            </Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              placeholder="Enter category name"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Icon
              {initialData && hasFieldChanged(icon || '', initialData.icon) && (
                <span style={{ color: 'orange', fontSize: '12px', marginLeft: '8px' }}>• Modified</span>
              )}
            </Form.Label>
            <div className="d-flex align-items-center gap-3">
              <Button variant="outline-secondary" onClick={() => fileInputRef.current?.click()} size="sm" disabled={isUploading}>Upload Icon</Button>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleIconChange}
                disabled={isUploading}
              />
              {icon && <img src={getImageUrl(icon)} alt="icon preview" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />}
              {isUploading && <span style={{ color: '#007bff' }}>Uploading...</span>}
            </div>
            {uploadError && <div className="text-danger mt-2">{uploadError}</div>}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Description
              {initialData && hasFieldChanged(description, initialData.description) && (
                <span style={{ color: 'orange', fontSize: '12px', marginLeft: '8px' }}>• Modified</span>
              )}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Enter category description"
            />
          </Form.Group>
          {error && <div className="text-danger mb-2">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={isUploading}>{initialData ? 'Save Changes' : 'Add Category'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditCategoryModal; 